import { commonComponents as cc } from "@/components/common";
import { JourneyProgressSummary, type JourneyProgressStep } from "@/components/pages/SuccessPage";
import { BILLING_PLAN_OPTIONS } from "@/features/admin/billing/adminBillingFormUtils";
import { InviteQrMark, InviteShareButtons } from "@/features/qr-code/components/QrCodeRoute";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import {
    Building2,
    FileText,
    Mail,
    Pencil,
    Plus,
    QrCode,
    Search,
    Send,
    ShieldCheck,
    Stethoscope,
    Tag,
    UserRound,
    UsersRound,
} from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/design/features/manager-study-clinician-invite-plan")({
    component: ManagerStudyClinicianInvitePlanDemo,
});

type DemoPersona = "admin" | "manager" | "clinician";
type DemoPageKind = "form" | "invite" | "success";

interface StudyConfig {
    id: string;
    studyName: string;
    labelAccess: string[];
    forms: string[];
    billingPlan: string;
    expiryDate: string;
    published: boolean;
}

interface StudyInvite {
    id: string;
    studyId: string;
    studyName: string;
    studyLabel: string;
    forms: string[];
    recipientEmail: string;
    status: "pending" | "used";
    sentAt: string;
    usedBy?: string;
    usedAt?: string;
}

interface ClinicianSignup {
    name: string;
    email: string;
    organisation: string;
    complete: boolean;
}

interface DemoPage {
    id: string;
    title: string;
    description?: string;
    kind: DemoPageKind;
    icon: ReactNode;
    nextLabel?: string;
}

interface AppliedAccess {
    label_access: string;
    form_access: string[];
    template_name: string;
    billing: string;
}

const FORM_OPTIONS = ["EQ-5D-5L", "Oxford Knee", "Study baseline", "KOOS"];

const LABEL_OPTIONS = ["Elite Tendon Knee", "Arthrosamid", "Bandajist", "Otivio"];

const MANAGER_LABEL_ACCESS = ["Elite Tendon Knee", "Arthrosamid"];

function primaryStudyLabel(study: Pick<StudyConfig, "labelAccess">): string {
    return study.labelAccess[0] ?? "";
}

function studyVisibleToManager(study: StudyConfig): boolean {
    return study.published && study.labelAccess.some((label) => MANAGER_LABEL_ACCESS.includes(label));
}

function billingPlanLabel(plan: string): string {
    if (!plan) return "—";
    return BILLING_PLAN_OPTIONS.find((option) => option.value === plan)?.label ?? plan;
}

function formatExpiryDate(date: string): string {
    if (!date) return "—";
    const parsed = new Date(`${date}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const DEFAULT_STUDY: StudyConfig = {
    id: "elite-tendon-knee-2026",
    studyName: "Elite Tendon Knee 2026",
    labelAccess: ["Elite Tendon Knee"],
    forms: ["EQ-5D-5L", "Oxford Knee", "Study baseline"],
    billingPlan: "enterprise",
    expiryDate: "2026-12-31",
    published: false,
};

const SECOND_STUDY: StudyConfig = {
    id: "arthrosamid-registry-2026",
    studyName: "Arthrosamid registry 2026",
    labelAccess: ["Arthrosamid"],
    forms: ["EQ-5D-5L", "KOOS"],
    billingPlan: "pro",
    expiryDate: "2027-06-30",
    published: true,
};

function buildDefaultInvites(study: StudyConfig): StudyInvite[] {
    return [
        {
            id: "invite-pending-1",
            studyId: study.id,
            studyName: study.studyName,
            studyLabel: primaryStudyLabel(study),
            forms: study.forms,
            recipientEmail: "sam.taylor@riverside.example",
            status: "pending",
            sentAt: "2 days ago",
        },
        {
            id: "invite-used-1",
            studyId: study.id,
            studyName: study.studyName,
            studyLabel: primaryStudyLabel(study),
            forms: study.forms,
            recipientEmail: "jane.hughes@northside.example",
            status: "used",
            sentAt: "1 week ago",
            usedBy: "Dr Jane Hughes",
            usedAt: "5 days ago",
        },
    ];
}

const ADMIN_PAGES: DemoPage[] = [
    {
        id: "studies-list",
        title: "Studies",
        description: "Create studies and invite managers or clinicians, similar to the templates library.",
        kind: "form",
        icon: <FileText className="w-5 h-5" />,
    },
    {
        id: "create-study",
        title: "New study",
        description: "Define label access, form access, and billing for this study.",
        kind: "form",
        icon: <ShieldCheck className="w-5 h-5" />,
        nextLabel: "Save & publish",
    },
    {
        id: "admin-invite",
        title: "Invite to study",
        description: "Share study access by email or QR code, like inviting from a template row.",
        kind: "invite",
        icon: <QrCode className="w-5 h-5" />,
        nextLabel: "Switch to manager view",
    },
];

const MANAGER_PAGES: DemoPage[] = [
    {
        id: "studies-list",
        title: "Studies you manage",
        description: "Studies are defined by label access. You only see studies for labels on your account.",
        kind: "form",
        icon: <Tag className="w-5 h-5" />,
    },
    {
        id: "invites-table",
        title: "Study invites",
        description: "Pending and used clinician invites for the selected study.",
        kind: "form",
        icon: <FileText className="w-5 h-5" />,
        nextLabel: "New invite",
    },
    {
        id: "invite-share",
        title: "Share study invite",
        description: "Send a pending invite by email or QR code. Used invites cannot be reshared.",
        kind: "invite",
        icon: <QrCode className="w-5 h-5" />,
        nextLabel: "Switch to clinician view",
    },
];

const CLINICIAN_PAGES: DemoPage[] = [
    {
        id: "welcome",
        title: "Join Elite Tendon Knee",
        description: "You have been invited as a clinician on Patient Watch.",
        kind: "form",
        icon: <Stethoscope className="w-5 h-5" />,
    },
    {
        id: "organisation",
        title: "Add your organisation",
        description: "Join your clinic or hospital before you complete your profile.",
        kind: "form",
        icon: <Building2 className="w-5 h-5" />,
        nextLabel: "Continue to profile",
    },
    {
        id: "name",
        title: "What is your name?",
        description: "This is how colleagues will see you in Patient Watch.",
        kind: "form",
        icon: <UserRound className="w-5 h-5" />,
    },
    {
        id: "email",
        title: "What is your email?",
        description: "We will use this for your account and study notifications.",
        kind: "form",
        icon: <Mail className="w-5 h-5" />,
        nextLabel: "Go to patients",
    },
    {
        id: "patients",
        title: "Patients",
        description:
            "After signup, clinicians land here with study label access and their study template ready to use.",
        kind: "success",
        icon: <UsersRound className="w-5 h-5" />,
    },
];

const PERSONA_META: Record<
    DemoPersona,
    { label: string; badge: string; description: string; pages: DemoPage[]; journeyStages: string[] }
> = {
    admin: {
        label: "Admin",
        badge: "Studies",
        description: "Browse studies in a table, create new ones, and invite users to a study.",
        pages: ADMIN_PAGES,
        journeyStages: ["Studies", "Create", "Invite"],
    },
    manager: {
        label: "Manager",
        badge: "Send invite",
        description: "Browse studies by label access, manage invites in a table, then share pending invites.",
        pages: MANAGER_PAGES,
        journeyStages: ["Studies", "Invites", "Share"],
    },
    clinician: {
        label: "Clinician",
        badge: "Sign up",
        description: "Add an organisation, complete profile details, then arrive on the patients page.",
        pages: CLINICIAN_PAGES,
        journeyStages: ["Organisation", "Profile", "Patients"],
    },
};

function buildInviteUrl(study: StudyConfig, invite: StudyInvite) {
    const params = new URLSearchParams({
        quickstart_type: "STUDY_CLINICIAN_INVITE",
        role: "CLINICIAN",
        study_id: study.id,
        study_label: primaryStudyLabel(study),
        form_access: study.forms.join(",").toLowerCase().replace(/\s+/g, "-"),
        label_access: study.labelAccess.join(","),
        billing: study.billingPlan,
        billing_account_id: study.billingPlan,
        onboarding: "staff_paid_account",
        invite_id: invite.id,
    });

    if (typeof window === "undefined") {
        return `/signup?${params.toString()}`;
    }

    return `${window.location.origin}/signup?${params.toString()}`;
}

function buildJourneySteps(persona: DemoPersona, pageIndex: number): JourneyProgressStep[] {
    const stages = PERSONA_META[persona].journeyStages;
    const pages = PERSONA_META[persona].pages;
    const currentPage = pages[pageIndex] ?? pages[0];
    const activeStage =
        persona === "clinician"
            ? currentPage.id === "patients"
                ? "Patients"
                : currentPage.id === "organisation" || currentPage.id === "welcome"
                  ? "Organisation"
                  : "Profile"
            : persona === "manager"
              ? currentPage.id === "invite-share"
                  ? "Share"
                  : currentPage.id === "invites-table"
                    ? "Invites"
                    : "Studies"
              : currentPage.id === "admin-invite"
                ? "Invite"
                : currentPage.id === "create-study"
                  ? "Create"
                  : "Studies";

    const activeIndex = stages.indexOf(activeStage);

    return stages.map((stage, index) => ({
        label: stage,
        state: index === activeIndex ? "current" : index < activeIndex ? "complete" : "upcoming",
        value: index < activeIndex ? 100 : index === activeIndex ? 50 : 0,
    }));
}

function FormBadges({ forms, className }: { forms: string[]; className?: string }) {
    if (forms.length === 0) {
        return <span className={cn("text-muted-foreground text-base", className)}>—</span>;
    }

    return (
        <div className={cn("flex flex-wrap gap-2 py-0.5 min-w-0", className)}>
            {forms.map((form) => (
                <ui.Badge key={form} variant="secondary" className="px-2.5 py-0.5 font-normal text-sm">
                    {form}
                </ui.Badge>
            ))}
        </div>
    );
}

function DemoTableHeader({ columns, gridClassName }: { columns: string[]; gridClassName: string }) {
    return (
        <div
            role="row"
            className={cn(
                gridClassName,
                "items-center border-b bg-muted/30 font-medium text-muted-foreground text-base sm:text-base"
            )}
        >
            {columns.map((column) => (
                <div key={column} role="columnheader" className="px-3 sm:px-5 py-4 min-w-0">
                    {column}
                </div>
            ))}
        </div>
    );
}

const STUDIES_TABLE_GRID =
    "grid w-full min-w-0 grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,2fr)_minmax(0,0.7fr)_minmax(0,0.7fr)] items-start gap-x-0";

const ADMIN_STUDIES_TABLE_GRID =
    "grid w-full min-w-0 grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.7fr)_minmax(0,0.75fr)_minmax(0,0.6fr)_minmax(0,1fr)] items-start gap-x-0";

const INVITES_TABLE_GRID =
    "grid w-full min-w-0 grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.75fr)_minmax(0,0.85fr)_minmax(7rem,7rem)] items-start gap-x-0";

function DemoAdminStudiesTable({
    studies,
    invites,
    searchQuery,
    onSearchChange,
    onCreateStudy,
    onEditStudy,
    onInviteStudy,
}: {
    studies: StudyConfig[];
    invites: StudyInvite[];
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onCreateStudy: () => void;
    onEditStudy: (studyId: string) => void;
    onInviteStudy: (studyId: string) => void;
}) {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const visibleStudies = studies.filter((study) => {
        if (!normalizedQuery) return true;
        const haystack = [
            study.studyName,
            ...study.labelAccess,
            study.billingPlan,
            study.expiryDate,
            billingPlanLabel(study.billingPlan),
            ...study.forms,
        ]
            .join(" ")
            .toLowerCase();
        return haystack.includes(normalizedQuery);
    });

    return (
        <div className="space-y-4">
            <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center gap-3">
                <div className="relative flex-1 max-w-lg">
                    <Search className="top-1/2 left-3 absolute w-4 h-4 text-muted-foreground -translate-y-1/2 pointer-events-none" />
                    <ui.Input
                        value={searchQuery}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder="Search studies, labels, or forms"
                        className="pl-9"
                        aria-label="Search studies"
                    />
                </div>
                <ui.Button type="button" size="sm" className="gap-2 shrink-0" onClick={onCreateStudy}>
                    <Plus className="w-4 h-4" />
                    New study
                </ui.Button>
            </div>

            {visibleStudies.length === 0 ? (
                <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                    <ui.CardContent className="py-8 text-muted-foreground text-base text-center">
                        {studies.length === 0
                            ? "No studies yet. Create your first study to configure label and form access."
                            : "No studies match your search."}
                    </ui.CardContent>
                </ui.Card>
            ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden" role="table" aria-label="Studies">
                    <div role="rowgroup">
                        <DemoTableHeader
                            columns={["Label", "Study", "Forms", "Plan", "Expiry", "Status", "Actions"]}
                            gridClassName={ADMIN_STUDIES_TABLE_GRID}
                        />
                    </div>
                    <div role="rowgroup">
                        {visibleStudies.map((study) => {
                            const pendingCount = invites.filter(
                                (invite) => invite.studyId === study.id && invite.status === "pending"
                            ).length;

                            return (
                                <div
                                    key={study.id}
                                    role="row"
                                    className={cn(
                                        ADMIN_STUDIES_TABLE_GRID,
                                        "border-b border-b-slate-200 last:border-b-0 hover:bg-muted/30 transition-colors"
                                    )}
                                >
                                    <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                        <FormBadges forms={study.labelAccess} />
                                    </div>
                                    <div
                                        role="cell"
                                        className="px-3 sm:px-5 py-4 min-w-0 font-medium text-foreground text-base"
                                    >
                                        {study.studyName || "Untitled study"}
                                    </div>
                                    <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                        <FormBadges forms={study.forms} />
                                    </div>
                                    <div
                                        role="cell"
                                        className="px-3 sm:px-5 py-4 min-w-0 text-muted-foreground text-base truncate"
                                    >
                                        {billingPlanLabel(study.billingPlan)}
                                    </div>
                                    <div
                                        role="cell"
                                        className="px-3 sm:px-5 py-4 min-w-0 tabular-nums text-muted-foreground text-base"
                                    >
                                        {formatExpiryDate(study.expiryDate)}
                                    </div>
                                    <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                        <ui.Badge
                                            variant={study.published ? "secondary" : "outline"}
                                            className={cn(
                                                "font-normal",
                                                study.published
                                                    ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-50"
                                                    : "bg-amber-50 text-amber-800 hover:bg-amber-50"
                                            )}
                                        >
                                            {study.published ? "Published" : "Draft"}
                                        </ui.Badge>
                                    </div>
                                    <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                        <div className="flex flex-wrap gap-1.5">
                                            <ui.Button
                                                type="button"
                                                className="gap-1.5"
                                                onClick={() => onInviteStudy(study.id)}
                                            >
                                                <Send className="w-3.5 h-3.5" />
                                                Invite
                                                {pendingCount > 0 ? (
                                                    <span className="opacity-80 tabular-nums">({pendingCount})</span>
                                                ) : null}
                                            </ui.Button>
                                            <ui.Button
                                                type="button"
                                                variant="outline"
                                                className="gap-1.5"
                                                onClick={() => onEditStudy(study.id)}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                                Edit
                                            </ui.Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function DemoStudiesTable({
    studies,
    invites,
    onSelectStudy,
}: {
    studies: StudyConfig[];
    invites: StudyInvite[];
    onSelectStudy: (studyId: string) => void;
}) {
    const visibleStudies = studies.filter((study) => studyVisibleToManager(study));

    if (visibleStudies.length === 0) {
        return (
            <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                <ui.CardContent className="py-8 text-muted-foreground text-base text-center">
                    No published studies match your label access yet.
                </ui.CardContent>
            </ui.Card>
        );
    }

    return (
        <div className="border border-slate-200 rounded-xl overflow-hidden" role="table" aria-label="Studies">
            <div role="rowgroup">
                <DemoTableHeader
                    columns={["Label", "Study", "Forms", "Pending", "Used"]}
                    gridClassName={STUDIES_TABLE_GRID}
                />
            </div>
            <div role="rowgroup">
                {visibleStudies.map((study) => {
                    const studyInvites = invites.filter((invite) => invite.studyId === study.id);
                    const pendingCount = studyInvites.filter((invite) => invite.status === "pending").length;
                    const usedCount = studyInvites.filter((invite) => invite.status === "used").length;

                    return (
                        <div
                            key={study.id}
                            role="row"
                            tabIndex={0}
                            className={cn(
                                STUDIES_TABLE_GRID,
                                "cursor-pointer border-b border-b-slate-200 last:border-b-0 hover:bg-muted/50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset"
                            )}
                            onClick={() => onSelectStudy(study.id)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onSelectStudy(study.id);
                                }
                            }}
                        >
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                <FormBadges forms={study.labelAccess} />
                            </div>
                            <div
                                role="cell"
                                className="px-3 sm:px-5 py-4 min-w-0 font-medium text-foreground text-base"
                            >
                                {study.studyName}
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                <FormBadges forms={study.forms} />
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 tabular-nums text-foreground text-base">
                                {pendingCount}
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 tabular-nums text-foreground text-base">
                                {usedCount}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function DemoStudyInvitesTable({
    invites,
    onOpenInvite,
}: {
    invites: StudyInvite[];
    onOpenInvite: (inviteId: string) => void;
}) {
    if (invites.length === 0) {
        return (
            <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                <ui.CardContent className="py-8 text-muted-foreground text-base text-center">
                    No invites yet for this study.
                </ui.CardContent>
            </ui.Card>
        );
    }

    return (
        <div className="border border-slate-200 rounded-xl overflow-hidden" role="table" aria-label="Study invites">
            <div role="rowgroup">
                <DemoTableHeader
                    columns={["Forms", "Label", "Recipient", "Status", "Sent", "Actions"]}
                    gridClassName={INVITES_TABLE_GRID}
                />
            </div>
            <div role="rowgroup">
                {invites.map((invite) => {
                    const isPending = invite.status === "pending";

                    return (
                        <div
                            key={invite.id}
                            role="row"
                            tabIndex={0}
                            className={cn(
                                INVITES_TABLE_GRID,
                                "border-b border-b-slate-200 last:border-b-0 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset",
                                isPending ? "cursor-pointer hover:bg-muted/50" : "bg-slate-50/70 hover:bg-slate-50"
                            )}
                            onClick={() => onOpenInvite(invite.id)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onOpenInvite(invite.id);
                                }
                            }}
                        >
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                <FormBadges forms={invite.forms} />
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0 text-foreground text-base">
                                {invite.studyLabel}
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0 text-foreground text-base truncate">
                                {invite.recipientEmail}
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                <ui.Badge
                                    variant={isPending ? "secondary" : "outline"}
                                    className={cn(
                                        "font-normal",
                                        isPending
                                            ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                                            : "bg-emerald-50 text-emerald-800"
                                    )}
                                >
                                    {isPending ? "Pending" : "Used"}
                                </ui.Badge>
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 text-muted-foreground text-base">
                                {invite.sentAt}
                            </div>
                            <div role="cell" className="px-3 sm:px-5 py-4 min-w-0">
                                <ui.Button
                                    type="button"
                                    variant={isPending ? "default" : "outline"}
                                    className="gap-1.5"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onOpenInvite(invite.id);
                                    }}
                                >
                                    {isPending ? (
                                        <>
                                            <Send className="w-3.5 h-3.5" />
                                            Share
                                        </>
                                    ) : (
                                        "View"
                                    )}
                                </ui.Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function DemoPatientsPreview({
    clinicianName,
    organisation,
    applied,
}: {
    clinicianName: string;
    organisation: string;
    applied: AppliedAccess;
}) {
    const demoPatients = [
        { name: "James Porter", procedure: "Elite Tendon repair", start: "12 Jun 2026" },
        { name: "Sarah Ahmed", procedure: "Elite Tendon review", start: "3 Jun 2026" },
        { name: "Michael Chen", procedure: "Baseline assessment", start: "28 May 2026" },
    ];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
                <ui.Badge className="bg-emerald-100 hover:bg-emerald-100 text-emerald-800">
                    Label: {applied.label_access}
                </ui.Badge>
                <ui.Badge variant="outline">{applied.template_name}</ui.Badge>
            </div>

            <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                <ui.CardContent className="py-3 text-slate-600 text-sm leading-relaxed">
                    Signed in as {clinicianName} at {organisation}. Form access includes{" "}
                    {applied.form_access.join(", ")}.
                </ui.CardContent>
            </ui.Card>

            <div className="flex flex-wrap gap-2">
                {(["Active", "Pending", "Inactive"] as const).map((tab, index) => (
                    <ui.Button key={tab} size="sm" variant={index === 0 ? "default" : "outline"}>
                        {tab}
                    </ui.Button>
                ))}
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="gap-0 grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.1fr)_minmax(0,0.8fr)] bg-muted/30 px-4 py-3.5 font-medium text-muted-foreground text-sm sm:text-base">
                    <span>Patient</span>
                    <span>Procedure</span>
                    <span>Start</span>
                </div>
                {demoPatients.map((patient) => (
                    <div
                        key={patient.name}
                        className="gap-0 grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.1fr)_minmax(0,0.8fr)] hover:bg-muted/40 px-4 py-4 border-slate-200 border-t text-base transition-colors"
                    >
                        <span className="font-medium text-foreground truncate">{patient.name}</span>
                        <span className="text-muted-foreground truncate">{patient.procedure}</span>
                        <span className="tabular-nums text-muted-foreground">{patient.start}</span>
                    </div>
                ))}
            </div>

            <p className="text-muted-foreground text-xs leading-relaxed">
                This matches where a clinician lands after onboarding: the `/patients` page, scoped to their study label
                and template.
            </p>
        </div>
    );
}

function DemoScreen({
    persona,
    page,
    pageIndex,
    studies,
    allInvites,
    selectedStudy,
    selectedInvite,
    studyInvites,
    clinician,
    applied,
    adminSearchQuery,
    studyFormMode,
    onStudyChange,
    onClinicianChange,
    onSelectStudy,
    onSelectInvite,
    onCreateInvite,
    onAdminSearchChange,
    onAdminCreateStudy,
    onAdminEditStudy,
    onAdminInviteStudy,
    onSaveStudy,
    onNext,
    onBack,
    onSwitchPersona,
    onMarkInviteUsed,
}: {
    persona: DemoPersona;
    page: DemoPage;
    pageIndex: number;
    studies: StudyConfig[];
    allInvites: StudyInvite[];
    selectedStudy: StudyConfig;
    selectedInvite: StudyInvite | null;
    studyInvites: StudyInvite[];
    clinician: ClinicianSignup;
    applied: AppliedAccess | null;
    adminSearchQuery: string;
    studyFormMode: "create" | "edit";
    onStudyChange: (study: StudyConfig) => void;
    onClinicianChange: (clinician: ClinicianSignup) => void;
    onSelectStudy: (studyId: string) => void;
    onSelectInvite: (inviteId: string) => void;
    onCreateInvite: () => void;
    onAdminSearchChange: (value: string) => void;
    onAdminCreateStudy: () => void;
    onAdminEditStudy: (studyId: string) => void;
    onAdminInviteStudy: (studyId: string) => void;
    onSaveStudy: () => void;
    onNext: () => void;
    onBack: () => void;
    onSwitchPersona: (persona: DemoPersona) => void;
    onMarkInviteUsed: (inviteId: string) => void;
}) {
    const qrWrapRef = useRef<HTMLDivElement>(null);
    const inviteUrl = useMemo(
        () => (selectedInvite ? buildInviteUrl(selectedStudy, selectedInvite) : ""),
        [selectedStudy, selectedInvite]
    );
    const pages = PERSONA_META[persona].pages;
    const isFirst = pageIndex === 0;
    const isLast = pageIndex === pages.length - 1;
    const journeySteps = buildJourneySteps(persona, pageIndex);
    const currentStage = journeySteps.find((step) => step.state === "current")?.label ?? journeySteps[0]?.label ?? "";
    const selectedStudyLabel = primaryStudyLabel(selectedStudy);

    const renderBody = () => {
        if (persona === "admin" && page.id === "studies-list") {
            return (
                <DemoAdminStudiesTable
                    studies={studies}
                    invites={allInvites}
                    searchQuery={adminSearchQuery}
                    onSearchChange={onAdminSearchChange}
                    onCreateStudy={onAdminCreateStudy}
                    onEditStudy={onAdminEditStudy}
                    onInviteStudy={onAdminInviteStudy}
                />
            );
        }

        if (persona === "admin" && page.id === "create-study") {
            return (
                <div className="space-y-6">
                    <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                        <ui.CardContent className="py-3 text-slate-600 text-sm leading-relaxed">
                            {studyFormMode === "create"
                                ? "Define label access, form access, and billing. Published studies appear for managers with matching label access."
                                : "Update study settings. Saving keeps the study published for managers with matching label access."}
                        </ui.CardContent>
                    </ui.Card>

                    <div className="space-y-2">
                        <ui.Label htmlFor="study-name">Study name</ui.Label>
                        <ui.Input
                            id="study-name"
                            value={selectedStudy.studyName}
                            onChange={(event) => onStudyChange({ ...selectedStudy, studyName: event.target.value })}
                            placeholder="e.g. Elite Tendon Knee 2026"
                        />
                    </div>

                    <div className="space-y-2">
                        <ui.Label>Label access</ui.Label>
                        <ui.MultiSelect
                            options={LABEL_OPTIONS.map((label) => ({ label, value: label }))}
                            value={selectedStudy.labelAccess}
                            onValueChange={(values) => onStudyChange({ ...selectedStudy, labelAccess: values })}
                            placeholder="Select labels"
                            className="w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <ui.Label>Form access</ui.Label>
                        <ui.MultiSelect
                            options={FORM_OPTIONS.map((form) => ({ label: form, value: form }))}
                            value={selectedStudy.forms}
                            onValueChange={(values) => onStudyChange({ ...selectedStudy, forms: values })}
                            placeholder="Select forms"
                            className="w-full"
                        />
                    </div>

                    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                        <div className="space-y-2">
                            <ui.Label>Billing plan</ui.Label>
                            <ui.Select
                                value={selectedStudy.billingPlan || undefined}
                                onValueChange={(value) => onStudyChange({ ...selectedStudy, billingPlan: value })}
                            >
                                <ui.SelectTrigger id="billing-plan">
                                    <ui.SelectValue placeholder="Select plan" />
                                </ui.SelectTrigger>
                                <ui.SelectContent>
                                    {BILLING_PLAN_OPTIONS.map((option) => (
                                        <ui.SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </ui.SelectItem>
                                    ))}
                                </ui.SelectContent>
                            </ui.Select>
                        </div>
                        <div className="space-y-2">
                            <ui.Label htmlFor="study-expiry">Expiry date</ui.Label>
                            <ui.Input
                                id="study-expiry"
                                type="date"
                                value={selectedStudy.expiryDate}
                                onChange={(event) =>
                                    onStudyChange({ ...selectedStudy, expiryDate: event.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>
            );
        }

        if (persona === "admin" && page.id === "admin-invite") {
            if (!selectedInvite) {
                return null;
            }

            return (
                <div className="space-y-4">
                    <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                        <ui.CardHeader className="pb-2">
                            <ui.CardTitle className="text-xl normal-case">{selectedStudy.studyName}</ui.CardTitle>
                            <ui.CardDescription>
                                Invite a manager or clinician to{" "}
                                <strong>{selectedStudy.labelAccess.join(", ") || "this study"}</strong>. Form access:{" "}
                                {selectedStudy.forms.join(", ") || "none selected yet"}. Plan:{" "}
                                {billingPlanLabel(selectedStudy.billingPlan)}. Expires{" "}
                                {formatExpiryDate(selectedStudy.expiryDate)}.
                            </ui.CardDescription>
                        </ui.CardHeader>
                    </ui.Card>

                    <div className="gap-8 grid grid-cols-1 lg:grid-cols-2">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-500" />
                                <p className="font-semibold text-slate-800 text-base">Email invite</p>
                            </div>
                            <div className="space-y-2">
                                <ui.Label htmlFor="admin-invite-email">Recipient email</ui.Label>
                                <ui.Input
                                    id="admin-invite-email"
                                    type="email"
                                    defaultValue={selectedInvite.recipientEmail}
                                    placeholder="manager@clinic.example"
                                />
                            </div>
                            <ui.Button type="button" className="gap-2 w-full sm:w-auto">
                                <Send className="w-4 h-4" />
                                Send invite email
                            </ui.Button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <QrCode className="w-4 h-4 text-slate-500" />
                                <p className="font-semibold text-slate-800 text-base">QR code invite</p>
                            </div>
                            <div className="flex justify-start">
                                <InviteQrMark
                                    destinationUrl={inviteUrl}
                                    title={`Join ${selectedStudyLabel}`}
                                    variant="compact"
                                    containerRef={qrWrapRef}
                                />
                            </div>
                            <InviteShareButtons destinationUrl={inviteUrl} qrContainerRef={qrWrapRef} />
                        </div>
                    </div>
                </div>
            );
        }

        if (persona === "manager" && page.id === "studies-list") {
            return (
                <div className="space-y-4">
                    <p className="text-slate-600 text-base leading-relaxed">
                        Your manager account has access to{" "}
                        {MANAGER_LABEL_ACCESS.map((label) => (
                            <ui.Badge key={label} variant="outline" className="mx-1 font-normal">
                                {label}
                            </ui.Badge>
                        ))}{" "}
                        labels. Each study can include one or more labels.
                    </p>
                    <DemoStudiesTable studies={studies} invites={allInvites} onSelectStudy={onSelectStudy} />
                </div>
            );
        }

        if (persona === "manager" && page.id === "invites-table") {
            return (
                <div className="space-y-4">
                    <ui.Card className="bg-slate-50 shadow-none border-slate-200">
                        <ui.CardHeader className="pb-2">
                            <ui.CardTitle className="text-xl normal-case">{selectedStudy.studyName}</ui.CardTitle>
                            <ui.CardDescription>
                                Label: {selectedStudy.labelAccess.join(", ") || "—"}. Open a pending invite to share it,
                                or view a used invite.
                            </ui.CardDescription>
                        </ui.CardHeader>
                    </ui.Card>
                    <DemoStudyInvitesTable invites={studyInvites} onOpenInvite={onSelectInvite} />
                </div>
            );
        }

        if (persona === "manager" && page.id === "invite-share") {
            if (!selectedInvite) {
                return null;
            }

            if (selectedInvite.status === "used") {
                return (
                    <div className="space-y-4">
                        <ui.Card className="bg-emerald-50 shadow-none border-emerald-200">
                            <ui.CardHeader>
                                <ui.CardTitle className="text-xl normal-case">Invite already used</ui.CardTitle>
                                <ui.CardDescription>
                                    {selectedInvite.usedBy} joined on {selectedInvite.usedAt}. This invite cannot be
                                    reshared.
                                </ui.CardDescription>
                            </ui.CardHeader>
                            <ui.CardContent className="space-y-3 text-base">
                                <div>
                                    <span className="text-muted-foreground">Recipient:</span>{" "}
                                    {selectedInvite.recipientEmail}
                                </div>
                                <FormBadges forms={selectedInvite.forms} />
                            </ui.CardContent>
                        </ui.Card>
                    </div>
                );
            }

            return (
                <div className="gap-8 grid grid-cols-1 lg:grid-cols-2">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-500" />
                            <p className="font-semibold text-slate-800 text-base">Email invite</p>
                        </div>
                        <div className="space-y-2">
                            <ui.Label htmlFor="invite-email">Clinician email</ui.Label>
                            <ui.Input
                                id="invite-email"
                                type="email"
                                defaultValue={selectedInvite.recipientEmail}
                                placeholder="clinician@clinic.example"
                            />
                        </div>
                        <ui.Button type="button" className="gap-2 w-full sm:w-auto">
                            <Send className="w-4 h-4" />
                            Send invite email
                        </ui.Button>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <QrCode className="w-4 h-4 text-slate-500" />
                            <p className="font-semibold text-slate-800 text-base">QR code invite</p>
                        </div>
                        <div className="flex justify-start">
                            <InviteQrMark
                                destinationUrl={inviteUrl}
                                title={`Join ${selectedStudyLabel}`}
                                variant="compact"
                                containerRef={qrWrapRef}
                            />
                        </div>
                        <InviteShareButtons destinationUrl={inviteUrl} qrContainerRef={qrWrapRef} />
                    </div>
                </div>
            );
        }

        if (persona === "clinician" && page.id === "welcome") {
            return (
                <ui.Card className="bg-indigo-50 shadow-none border-indigo-200">
                    <ui.CardHeader>
                        <ui.CardTitle className="text-xl normal-case">Study clinician invite</ui.CardTitle>
                        <ui.CardDescription>
                            You are joining <strong>{selectedStudyLabel}</strong> as a clinician, not as a patient on a
                            diary template.
                        </ui.CardDescription>
                    </ui.CardHeader>
                    <ui.CardContent className="space-y-2 text-slate-600 text-base">
                        <p>After signup you will receive:</p>
                        <ul className="space-y-1 pl-4 list-disc">
                            <li>Add your organisation first</li>
                            <li>Complete your profile details</li>
                            <li>Land on the patients page with label access for {selectedStudyLabel}</li>
                        </ul>
                    </ui.CardContent>
                </ui.Card>
            );
        }

        if (persona === "clinician" && page.id === "name") {
            return (
                <div className="space-y-2">
                    <ui.Label htmlFor="clinician-name">Name</ui.Label>
                    <ui.Input
                        id="clinician-name"
                        value={clinician.name}
                        onChange={(event) => onClinicianChange({ ...clinician, name: event.target.value })}
                        placeholder="e.g. Dr Sam Taylor"
                    />
                </div>
            );
        }

        if (persona === "clinician" && page.id === "email") {
            return (
                <div className="space-y-2">
                    <ui.Label htmlFor="clinician-email">Email</ui.Label>
                    <ui.Input
                        id="clinician-email"
                        type="email"
                        value={clinician.email}
                        onChange={(event) => onClinicianChange({ ...clinician, email: event.target.value })}
                    />
                </div>
            );
        }

        if (persona === "clinician" && page.id === "organisation") {
            return (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <ui.Label htmlFor="clinician-organisation">Organisation</ui.Label>
                        <ui.Input
                            id="clinician-organisation"
                            value={clinician.organisation}
                            onChange={(event) => onClinicianChange({ ...clinician, organisation: event.target.value })}
                            placeholder="e.g. Riverside Orthopaedic Clinic"
                        />
                    </div>
                    <p className="text-slate-500 text-base">
                        Same pattern as paid account signup - staff add their organisation before completing profile
                        details.
                    </p>
                </div>
            );
        }

        if (persona === "clinician" && page.id === "patients") {
            return applied ? (
                <DemoPatientsPreview
                    clinicianName={clinician.name}
                    organisation={clinician.organisation}
                    applied={applied}
                />
            ) : null;
        }

        return null;
    };

    const handlePrimary = () => {
        if (persona === "admin" && page.id === "create-study") {
            onSaveStudy();
            return;
        }

        if (persona === "admin" && page.id === "admin-invite") {
            onSwitchPersona("manager");
            return;
        }

        if (persona === "manager" && page.id === "studies-list") {
            const firstStudy = studies.find((study) => studyVisibleToManager(study));
            if (firstStudy) {
                onSelectStudy(firstStudy.id);
            }
            return;
        }

        if (persona === "manager" && page.id === "invites-table") {
            onCreateInvite();
            return;
        }

        if (persona === "manager" && page.id === "invite-share") {
            if (selectedInvite?.status === "pending") {
                onMarkInviteUsed(selectedInvite.id);
                onSwitchPersona("clinician");
            } else {
                onBack();
            }
            return;
        }

        if (persona === "clinician" && page.id === "email") {
            onClinicianChange({ ...clinician, complete: true });
            onNext();
            return;
        }

        onNext();
    };

    const primaryLabel =
        page.nextLabel ??
        (persona === "admin" && page.id === "create-study"
            ? studyFormMode === "create"
                ? "Save & publish"
                : "Save changes"
            : persona === "manager" && page.id === "studies-list"
              ? "Open first study"
              : persona === "manager" && page.id === "invites-table"
                ? "New invite"
                : persona === "manager" && page.id === "invite-share"
                  ? selectedInvite?.status === "used"
                      ? "Back to invites"
                      : "Switch to clinician view"
                  : persona === "clinician" && page.id === "email"
                    ? "Go to patients"
                    : isLast
                      ? "Done"
                      : "Next");

    const showFooter =
        !(persona === "clinician" && page.id === "patients") && !(persona === "admin" && page.id === "studies-list");
    const isDesktopPreview =
        persona === "admin" || persona === "manager" || (persona === "clinician" && page.id === "patients");

    const pageTitle =
        persona === "admin" && page.id === "create-study"
            ? studyFormMode === "create"
                ? "New study"
                : "Edit study"
            : page.title;

    return (
        <section className={cn("mx-auto w-full", isDesktopPreview ? "max-w-full" : "max-w-lg")}>
            <div className="flex flex-col gap-y-8 w-full">
                {page.id !== "patients" ? (
                    <JourneyProgressSummary currentStageLabel={currentStage} steps={journeySteps} />
                ) : null}
                <cc.Title words={pageTitle} description={page.description} headingVariant="compact" />
                <div className="space-y-8">
                    {renderBody()}
                    {showFooter ? (
                        <cc.FooterButtons
                            func1={handlePrimary}
                            func2={isFirst ? undefined : onBack}
                            words1={primaryLabel}
                            words2={isFirst ? undefined : "Back"}
                            primaryAction="next"
                            secondaryAction="back"
                            type1="button"
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
}

function createEmptyStudy(): StudyConfig {
    return {
        id: `study-${Date.now()}`,
        studyName: "",
        labelAccess: [],
        forms: [],
        billingPlan: "",
        expiryDate: "",
        published: false,
    };
}

function ManagerStudyClinicianInvitePlanDemo() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        {
            label: "Study clinician invites",
            route: { to: "/design/features/manager-study-clinician-invite-plan" },
        },
    ]);

    const [persona, setPersona] = useState<DemoPersona>("admin");
    const [pageIndexes, setPageIndexes] = useState<Record<DemoPersona, number>>({
        admin: 0,
        manager: 0,
        clinician: 0,
    });
    const [studies, setStudies] = useState<StudyConfig[]>([DEFAULT_STUDY, SECOND_STUDY]);
    const [selectedStudyId, setSelectedStudyId] = useState(DEFAULT_STUDY.id);
    const [selectedInviteId, setSelectedInviteId] = useState<string | null>(null);
    const [invites, setInvites] = useState<StudyInvite[]>(() => [
        ...buildDefaultInvites(DEFAULT_STUDY),
        ...buildDefaultInvites(SECOND_STUDY),
    ]);
    const [clinician, setClinician] = useState<ClinicianSignup>({
        name: "Dr Sam Taylor",
        email: "sam.taylor@riverside.example",
        organisation: "Riverside Orthopaedic Clinic",
        complete: false,
    });
    const [adminSearchQuery, setAdminSearchQuery] = useState("");
    const [studyFormMode, setStudyFormMode] = useState<"create" | "edit">("create");

    const selectedStudy = studies.find((study) => study.id === selectedStudyId) ?? DEFAULT_STUDY;
    const selectedInvite = invites.find((invite) => invite.id === selectedInviteId) ?? null;
    const studyInvites = invites.filter((invite) => invite.studyId === selectedStudyId);

    const pages = PERSONA_META[persona].pages;
    const pageIndex = pageIndexes[persona];
    const currentPage = pages[pageIndex] ?? pages[0];

    const applied: AppliedAccess | null = clinician.complete
        ? {
              label_access: selectedStudyLabel,
              form_access: selectedStudy.forms,
              template_name: `${selectedStudyLabel || "Study"} - clinician template`,
              billing: selectedStudy.billingPlan,
          }
        : null;

    const isDesktopPreview =
        persona === "admin" || persona === "manager" || (persona === "clinician" && currentPage.id === "patients");

    const goBack = () =>
        setPageIndexes((current) => ({
            ...current,
            [persona]: Math.max(0, current[persona] - 1),
        }));

    const goNext = () =>
        setPageIndexes((current) => ({
            ...current,
            [persona]: Math.min(pages.length - 1, current[persona] + 1),
        }));

    const switchPersona = (nextPersona: DemoPersona) => {
        setPersona(nextPersona);
        setPageIndexes((current) => ({ ...current, [nextPersona]: 0 }));
    };

    const updateStudy = (updatedStudy: StudyConfig) => {
        setStudies((current) => current.map((study) => (study.id === updatedStudy.id ? updatedStudy : study)));
    };

    const selectStudy = (studyId: string) => {
        setSelectedStudyId(studyId);
        setSelectedInviteId(null);
        setPageIndexes((current) => ({ ...current, manager: 1 }));
    };

    const selectInvite = (inviteId: string) => {
        const invite = invites.find((item) => item.id === inviteId);
        if (!invite) return;
        setSelectedStudyId(invite.studyId);
        setSelectedInviteId(inviteId);
        setPageIndexes((current) => ({ ...current, manager: 2 }));
    };

    const createInvite = () => {
        const newInvite: StudyInvite = {
            id: `invite-pending-${Date.now()}`,
            studyId: selectedStudy.id,
            studyName: selectedStudy.studyName,
            studyLabel: primaryStudyLabel(selectedStudy),
            forms: selectedStudy.forms,
            recipientEmail: "new.clinician@clinic.example",
            status: "pending",
            sentAt: "Just now",
        };
        setInvites((current) => [newInvite, ...current]);
        setSelectedInviteId(newInvite.id);
        setPageIndexes((current) => ({ ...current, manager: 2 }));
    };

    const markInviteUsed = (inviteId: string) => {
        setInvites((current) =>
            current.map((invite) =>
                invite.id === inviteId
                    ? {
                          ...invite,
                          status: "used",
                          usedBy: clinician.name,
                          usedAt: "Just now",
                      }
                    : invite
            )
        );
    };

    const ensurePendingInvite = (study: StudyConfig) => {
        const existingPending = invites.find((invite) => invite.studyId === study.id && invite.status === "pending");
        if (existingPending) {
            setSelectedInviteId(existingPending.id);
            return existingPending;
        }

        const newInvite: StudyInvite = {
            id: `invite-pending-${Date.now()}`,
            studyId: study.id,
            studyName: study.studyName,
            studyLabel: primaryStudyLabel(study),
            forms: study.forms,
            recipientEmail: "new.recipient@clinic.example",
            status: "pending",
            sentAt: "Just now",
        };
        setInvites((current) => [newInvite, ...current]);
        setSelectedInviteId(newInvite.id);
        return newInvite;
    };

    const startAdminCreateStudy = () => {
        const newStudy = createEmptyStudy();
        setStudies((current) => [newStudy, ...current]);
        setSelectedStudyId(newStudy.id);
        setStudyFormMode("create");
        setPageIndexes((current) => ({ ...current, admin: 1 }));
    };

    const startAdminEditStudy = (studyId: string) => {
        setSelectedStudyId(studyId);
        setStudyFormMode("edit");
        setPageIndexes((current) => ({ ...current, admin: 1 }));
    };

    const startAdminInviteStudy = (studyId: string) => {
        const study = studies.find((item) => item.id === studyId);
        if (!study) return;
        setSelectedStudyId(studyId);
        ensurePendingInvite(study);
        setPageIndexes((current) => ({ ...current, admin: 2 }));
    };

    const saveStudy = () => {
        updateStudy({ ...selectedStudy, published: true });
        setPageIndexes((current) => ({ ...current, admin: 0 }));
    };

    return (
        <section className="bg-gradient-to-br from-sky-50 via-slate-50 to-white min-h-screen">
            <div
                className={cn(
                    "gap-10 grid grid-cols-1 mx-auto px-4 sm:px-8 py-8 max-w-[96rem]",
                    isDesktopPreview
                        ? "lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]"
                        : "lg:grid-cols-[minmax(0,1fr)_580px]"
                )}
            >
                <div className="flex flex-col justify-center space-y-6">
                    <div className="space-y-4">
                        <ui.Badge className="bg-sky-100 hover:bg-sky-100 text-sky-700">
                            Developer demo - study clinician invites
                        </ui.Badge>
                        <div className="space-y-3 max-w-3xl">
                            <h1 className="font-bold text-slate-950 text-4xl lg:text-5xl tracking-tight">
                                Study clinician invites
                            </h1>
                            <p className="text-slate-600 text-lg leading-relaxed">
                                Preview the admin configuration, manager invite page, and clinician signup outcome in
                                one flow.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {(Object.keys(PERSONA_META) as DemoPersona[]).map((key) => (
                            <ui.Button
                                key={key}
                                type="button"
                                size="sm"
                                variant={persona === key ? "default" : "outline"}
                                onClick={() => switchPersona(key)}
                            >
                                {PERSONA_META[key].label}
                            </ui.Button>
                        ))}
                    </div>
                </div>

                <div className="lg:top-8 lg:sticky h-fit">
                    <div
                        className={cn(
                            "bg-white/70 shadow-xl backdrop-blur mx-auto border border-white overflow-y-auto",
                            isDesktopPreview
                                ? "p-8 lg:p-10 rounded-xl w-full min-h-[720px] lg:min-h-[780px]"
                                : "p-6 rounded-[2rem] w-full max-w-[480px] min-h-[680px]"
                        )}
                    >
                        <DemoScreen
                            persona={persona}
                            page={currentPage}
                            pageIndex={pageIndex}
                            studies={studies}
                            allInvites={invites}
                            selectedStudy={selectedStudy}
                            selectedInvite={selectedInvite}
                            studyInvites={studyInvites}
                            clinician={clinician}
                            applied={applied}
                            adminSearchQuery={adminSearchQuery}
                            studyFormMode={studyFormMode}
                            onStudyChange={updateStudy}
                            onClinicianChange={setClinician}
                            onSelectStudy={selectStudy}
                            onSelectInvite={selectInvite}
                            onCreateInvite={createInvite}
                            onAdminSearchChange={setAdminSearchQuery}
                            onAdminCreateStudy={startAdminCreateStudy}
                            onAdminEditStudy={startAdminEditStudy}
                            onAdminInviteStudy={startAdminInviteStudy}
                            onSaveStudy={saveStudy}
                            onNext={goNext}
                            onBack={goBack}
                            onSwitchPersona={switchPersona}
                            onMarkInviteUsed={markInviteUsed}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
